import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { PricingFeatureTable } from "@/components/wpl/pricing/src/features-table";
import { usePricingContentContext } from "@/components/wpl/pricing/src/pricing-content-context.tsx";
import {
    PricingFaq,
    QuotationsAdornment
} from "@/components/wpl/pricing/src/pricing-faq/index.ts";
import {
    PricingPageLayout,
    PageHeading
} from "@/components/wpl/pricing/src/pricing-page-layout";
import {
    CallToAction,
    PricingContent
} from "@/utils/pricing-queries/get-pricing-content-gql.ts";
import { ParentScrollTracking } from "@/utils/scroll-tracking/index.ts";
import { UserLocale } from "@/utils/useGeoCurrency.ts";

import { AppPricingData } from "../../../../contentful/index.tsx";

import { Hero } from "./sections/hero.tsx";
import { StrategyCollectionNavbar } from "./sections/strategy-collection-navbar.tsx";
import { StrategyCollectionPriceFinder } from "./sections/strategy-collection-price-finder.tsx";

export interface StrategyCollectionPricingPageTemplateProps {
    pricing?: AppPricingData;
    countryCode?: UserLocale;
    pricingContent?: PricingContent;
}

// eslint-disable-next-line complexity
export const StrategyCollectionPricingPageTemplate = (
    props: StrategyCollectionPricingPageTemplateProps
) => {
    // @ts-ignore
    const intl = useIntl();

    const content = usePricingContentContext();

    const featuresCtaContent = content
        ?.callToActionContainer?.[1] as CallToAction;
    // @ts-ignore
    const questionsCtaContent = content
        ?.callToActionContainer?.[0] as CallToAction;

    return (
        <PricingPageLayout
            nav={<StrategyCollectionNavbar />}
            showFallback={content?.hasError}
        >
            <ParentScrollTracking
                scrollTrackingEnabled={true}
                pageId="strategy-collection-pricing-page"
            >
                <div css={headingContainerCss}>
                    <Hero />
                    <PageHeading>{content?.headline}</PageHeading>
                </div>

                <StrategyCollectionPriceFinder
                    pricing={props.pricing}
                    countryCode={props.countryCode}
                />
                <PricingFeatureTable
                    title={content?.compareFeaturesTitle ?? undefined}
                    comparePlans={content?.comparePlans ?? undefined}
                    compareFeatures={content?.compareFeatures ?? undefined}
                    linkValues={{
                        linkText: featuresCtaContent?.text ?? undefined,
                        linkHref: featuresCtaContent?.url ?? undefined
                    }}
                    dataProductKey="strategyCollection"
                />
                <QuotationsAdornment>
                    <ParentScrollTracking>
                        <PricingFaq
                            header={content?.getMoreDetailsTitle ?? undefined}
                            planDetails={
                                content?.additionalDetails ?? undefined
                            }
                        />
                    </ParentScrollTracking>
                </QuotationsAdornment>
            </ParentScrollTracking>
        </PricingPageLayout>
    );
};

const headingContainerCss = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "16px"
});
